// src/screens/EditPostScreen.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './EditPostScreen.css';
import api from '../api';

import backIcon from '../assets/backspace-icon.png';
import PlanLimitDrawer from '../components/popups/PlanLimitDrawer';

export default function EditPostScreen() {
  const navigate = useNavigate();
  const { id } = useParams();

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('barterfi_user'));
    } catch {
      return null;
    }
  })();
  const currentUser = storedUser?.username || localStorage.getItem('username') || '';

  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);

  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const [myPlan, setMyPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  const [planLimitOpen, setPlanLimitOpen] = useState(false);
  const [limitMeta, setLimitMeta] = useState({
    feature: 'Post length',
    limit: 300,
    unit: 'characters',
  });

  const showError = (msg, ms = 3000) => {
    setErr(msg);
    window.clearTimeout(showError._t);
    showError._t = window.setTimeout(() => setErr(''), ms);
  };

  const isOwner = useMemo(() => {
    const u = (currentUser || '').toLowerCase();
    const p = (post?.username || post?.user?.username || '').toLowerCase();
    return u && p && u === p;
  }, [currentUser, post]);

  const isPollPost = post?.postType === 'poll';

  const refreshMyPlan = async () => {
    try {
      setPlanLoading(true);
      const res = await api.get('/plans/me');
      setMyPlan(res.data?.plan || null);
    } catch (err) {
      setMyPlan({
        id: 'FREE',
        label: 'Free',
        limits: { postTextMax: 300, postImagesMax: 3, postVideoMaxMB: 20 },
      });
    } finally {
      setPlanLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        setPlanLoading(true);
        const res = await api.get('/plans/me');
        if (!cancelled) setMyPlan(res.data?.plan || null);
      } catch (err) {
        if (!cancelled) {
          setMyPlan({
            id: 'FREE',
            label: 'Free',
            limits: { postTextMax: 300, postImagesMax: 3, postVideoMaxMB: 20 },
          });
        }
      } finally {
        if (!cancelled) setPlanLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onFocus = () => refreshMyPlan();
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshMyPlan();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  useEffect(() => {
    let stop = false;

    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/posts/${id}`);
        if (stop) return;

        setPost(data);
        setText(typeof data?.content === 'string' ? data.content : '');
      } catch (e) {
        if (stop) return;
        setErr('Failed to load post.');
      } finally {
        if (!stop) setLoading(false);
      }
    })();

    return () => {
      stop = true;
    };
  }, [id]);

  const postLengthLimit = Number(myPlan?.limits?.postTextMax ?? 300);
  const contentLen = (text || '').length;
  const remainingChars = postLengthLimit - contentLen;

  const canEditPost = (myPlan?.id || 'FREE') !== 'FREE';
  const isPostTooLong = (t) => (t || '').length > postLengthLimit;

  const textareaRef = useRef(null);
  const MAX_VH = 0.5;

  const adjustTextareaEl = (el) => {
    if (!el) return;
    const prev = el.style.transition;
    el.style.transition = 'none';
    el.style.height = 'auto';
    const maxPx = Math.floor(window.innerHeight * MAX_VH);
    const next = Math.min(el.scrollHeight, maxPx);
    el.style.height = next + 'px';
    el.style.overflowY = el.scrollHeight > maxPx ? 'auto' : 'hidden';
    requestAnimationFrame(() => {
      el.style.transition = prev || 'height 120ms ease';
    });
  };

  useEffect(() => {
    adjustTextareaEl(textareaRef.current);
    const onResize = () => adjustTextareaEl(textareaRef.current);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onSave = async () => {
    setErr('');

    if (!isOwner) {
      showError('You can only edit your own post.');
      return;
    }

    if (isPollPost) {
      showError('Polls cannot be edited after publishing.');
      return;
    }

    const clean = (text || '').trim();
    if (!clean) {
      showError('Post text cannot be empty.');
      return;
    }

    if (!canEditPost) {
      showError('Editing posts is not available on your plan.', 2500);

      setLimitMeta({
        feature: 'Edit Post',
        limit: 0,
        unit: '',
      });

      setPlanLimitOpen(true);
      return;
    }

    if (isPostTooLong(clean)) {
      showError(
        `Post too long. Max ${postLengthLimit} characters on ${myPlan?.label || 'Free'}.`
      );
      setLimitMeta({ feature: 'Post length', limit: postLengthLimit, unit: 'characters' });
      setPlanLimitOpen(true);
      return;
    }

    try {
      setSaving(true);

      const { data } = await api.patch(`/posts/${id}/text`, { content: clean });

      try {
        window.dispatchEvent(
          new CustomEvent('post:updated', {
            detail: {
              postId: String(id),
              post: data,
            },
          })
        );
      } catch {}

      sessionStorage.setItem('bf_post_edited', String(id));

      navigate(`/post/${id}`, { replace: true, state: { updatedPost: data } });
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        'Failed to save changes.';
      showError(msg, 3500);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="editpost">
        <div className="editpost-topbar">
          <button className="editpost-back" onClick={() => navigate(-1)}>
            <img src={backIcon} alt="Back" />
          </button>
          <div className="editpost-title">Edit Post</div>
        </div>
        <div className="editpost-body">Loading…</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="editpost">
        <div className="editpost-topbar">
          <button className="editpost-back" onClick={() => navigate(-1)}>
            <img src={backIcon} alt="Back" />
          </button>
          <div className="editpost-title">Edit Post</div>
        </div>
        <div className="editpost-body">{err || 'Post not found.'}</div>
      </div>
    );
  }

  return (
    <div className="editpost">
      <div className="editpost-topbar">
        <button className="editpost-back" onClick={() => navigate(-1)} disabled={saving}>
          <img src={backIcon} alt="Back" />
        </button>

        <div className="editpost-title">Edit Post</div>

        <button
          className="editpost-save"
          onClick={onSave}
          disabled={saving || !isOwner || planLoading || isPollPost}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="editpost-body">
        {!isOwner && <div className="editpost-warn">You can only edit your own post.</div>}

        {planLoading && <div className="editpost-warn">Loading your plan…</div>}

        {isPollPost && (
          <div className="editpost-warn">
            Polls cannot be edited after publishing.
          </div>
        )}

        <div className="editpost-textarea-wrap">
          <textarea
            ref={textareaRef}
            className="editpost-textarea"
            value={text}
            onChange={(e) => {
              if (isPollPost) return;

              adjustTextareaEl(e.target);
              const val = e.target.value;
              setText(val);

              if (val.length > postLengthLimit) {
                showError(
                  `Post too long. Max ${postLengthLimit} characters on ${myPlan?.label || 'Free'}.`,
                  1500
                );
              } else {
                setErr((prev) => (prev?.includes('Post too long') ? '' : prev));
              }
            }}
            placeholder="Update your post…"
            disabled={saving || !isOwner || isPollPost}
          />

          <div
            className={`editpost-char-count ${remainingChars <= 50 ? 'warn' : ''} ${
              remainingChars < 0 ? 'over' : ''
            }`}
          >
            {remainingChars}
          </div>
        </div>

        {err ? <div className="editpost-error">{err}</div> : null}
      </div>

      <PlanLimitDrawer
        open={planLimitOpen}
        onClose={() => setPlanLimitOpen(false)}
        limit={limitMeta.limit}
        planLabel={myPlan?.label || 'Free'}
        featureLabel={limitMeta.feature}
        unit={limitMeta.unit || 'characters'}
        message={
          limitMeta.feature === 'Edit Post'
            ? 'Editing posts is not available on your current plan.'
            : ''
        }
      />
    </div>
  );
}
